'use client';

import { z } from 'zod';
import { Box, Flex } from '@chakra-ui/react';

import { MyOmersBffSystemErrorLog, MyOmersBffSystemErrorLogSchema } from '@/server/types';
import { safeJsonParse } from '@/server/utils/safeWrappers';
import InternalServerErrorContainer from '@/containers/common/InternalServerErrorContainer';
import Text from '@/components/atomic/v1/Text';
import { Card } from '@/components/atomic/v1/card/Card';
import Button from '@/components/atomic/v1/button/Button';

type ErrorWithDigest = Error & { digest?: string };

const ServerErrorInfoSchema = z.object({
  errorMessage: z.string(),
  errorLog: MyOmersBffSystemErrorLogSchema
});

type ServerErrorInfo = {
  errorMessage: string;
  errorLog: MyOmersBffSystemErrorLog;
};

/**
 * Transforms an Error into a plain object matching the `ServerErrorInfo`
 * interface _only if_ the message property of the error is serialized data that
 * conforms to that shape.
 *
 * This is required because NextJS doesn't retain extra fields if we throw
 * custom Error types
 */
function transformNextError(error: ErrorWithDigest): ErrorWithDigest | ServerErrorInfo {
  const parsedError = safeJsonParse(error.message).unwrapOr(null);

  if (ServerErrorInfoSchema.safeParse(parsedError).success) {
    return JSON.parse(error.message) as ServerErrorInfo;
  }

  return error;
}

const RootError = ({ error, reset }: { error: ErrorWithDigest; reset: () => void }) => {
  const customError = transformNextError(error);

  if (customError instanceof Error) {
    // Handle normal `Error` objects here

    return (
      <Card
        // @ts-ignore
        headerText={
          <Flex gap={3} alignItems="center">
            <span>Something went wrong!</span>
            <Button onClick={() => reset()} variant="secondary">
              Try again
            </Button>
          </Flex>
        }
        // @ts-ignore
        colorStripColor="status/error/default"
        bodyContent={
          <Box>
            <Text>{customError.message}</Text>
            <Text>Error digest: {customError.digest}</Text>

            {process.env.NEXT_PUBLIC_APP_ENV !== 'prod' && (
              <Box my="8" overflowX="scroll" fontSize="small">
                <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(customError.stack, null, 2)}</pre>
              </Box>
            )}
          </Box>
        }
      />
    );
  }

  // Handle our custom `Error`s here
  return (
    <Card
      // @ts-ignore
      headerText={
        <Flex gap={3} alignItems="center">
          <span>Something went wrong!</span>
          <Button onClick={() => reset()} variant="secondary">
            Try again
          </Button>
        </Flex>
      }
      // @ts-ignore
      colorStripColor="status/error/default"
      bodyContent={
        <InternalServerErrorContainer errorLog={customError.errorLog} errorMessage={customError.errorMessage} />
      }
    />
  );
};

export default RootError;
